import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { computeDisplayMonthly, isProgramDeliverableCard } from '../../utils/programPricing';
import {
  DELIVERABLE_ORDER_DOC_ID,
  normalizeDeliverableOrderIds,
  sortCatalogByDeliverableOrder,
} from '../../utils/deliverableOrder';

/**
 * Notes-timeline row for a Start program the lead customized or chose.
 * Collapsed: name, price, billing cycle. Expanded: the Proposal tab's Deliverables grid,
 * green where the lead had the item selected.
 */

// Catalog icons, card order and billing-cycle discounts — one fetch shared by every row.
let catalogPromise = null;
const loadCatalog = () => {
  if (!catalogPromise) {
    catalogPromise = Promise.all([
      getDocs(collection(db, 'alaCarteItems')),
      getDoc(doc(db, 'settings', DELIVERABLE_ORDER_DOC_ID)),
      getDocs(collection(db, 'billingCycles')),
    ])
      .then(([itemsSnap, orderSnap, cyclesSnap]) => ({
        items: itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        orderIds: normalizeDeliverableOrderIds(orderSnap.exists() ? orderSnap.data()?.order : null),
        billingCycles: Object.fromEntries(cyclesSnap.docs.map((d) => [d.id, d.data()])),
      }))
      .catch((error) => {
        console.warn('[StartProgramActivityRow] failed to load catalog', error);
        catalogPromise = null;
        return null;
      });
  }
  return catalogPromise;
};

// ProgramDetail logs each deliverable as "Name" or "Name ×12".
const parseDeliverableLabel = (label) => {
  const match = /^(.*?)\s*×(\d+)$/.exec(String(label || '').trim());
  if (match) return { name: match[1].trim(), quantity: Number(match[2]) };
  return { name: String(label || '').trim(), quantity: 1 };
};

const normalizeName = (name) => (name || '').trim().toLowerCase();

const StartProgramActivityRow = ({ label, detail, stamp, Icon }) => {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadCatalog().then((data) => {
      if (!cancelled && data) setCatalog(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Logged name → quantity for everything the lead had selected (child features included).
  const selectedQuantities = useMemo(() => {
    const map = new Map();
    (Array.isArray(detail?.deliverables) ? detail.deliverables : []).forEach((label) => {
      const { name, quantity } = parseDeliverableLabel(label);
      if (name) map.set(normalizeName(name), quantity);
    });
    return map;
  }, [detail]);

  // The Proposal tab's card set, in its order, marked with what this lead had selected.
  const deliverables = useMemo(() => {
    if (!catalog || selectedQuantities.size === 0) return [];
    const cards = catalog.items.filter((item) =>
      isProgramDeliverableCard(item, selectedQuantities.has(normalizeName(item.name)))
    );
    return sortCatalogByDeliverableOrder(cards, catalog.orderIds).map((item) => ({
      id: item.id,
      name: (item.name || '').trim(),
      icon: item.icon,
      quantity: selectedQuantities.get(normalizeName(item.name)) ?? null,
    }));
  }, [catalog, selectedQuantities]);

  // Same figure the lead saw under Investment: the monthly total after the cycle's discount.
  const price = Number(detail?.pricePerMonth) || 0;
  const priceLabel = price
    ? `$${computeDisplayMonthly(price, detail?.billingCycle, catalog?.billingCycles).toLocaleString()}/mo`
    : null;
  const cycleLabel = detail?.billingCycle
    ? String(detail.billingCycle).charAt(0).toUpperCase() + String(detail.billingCycle).slice(1)
    : null;
  const summary = [priceLabel, cycleLabel].filter(Boolean).join(' · ');
  const canExpand = deliverables.length > 0;

  return (
    <div className="w-full rounded-lg border border-blue-800 bg-blue-600 text-white dark:border-blue-300 dark:bg-blue-700">
      <button
        type="button"
        onClick={() => canExpand && setOpen((v) => !v)}
        aria-expanded={canExpand ? open : undefined}
        className={`flex w-full flex-nowrap items-center gap-3 px-3 py-2 text-left ${
          canExpand ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/45 bg-blue-900 dark:border-white/50 dark:bg-blue-950">
          <Icon className="h-5 w-5 text-white" strokeWidth={1.75} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-white">{label}</span>
          {summary && <span className="truncate text-xs text-white/80">{summary}</span>}
        </div>
        <span className="shrink-0 text-xs font-semibold tabular-nums text-white/90">{stamp}</span>
        {canExpand && (
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-white transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>

      {open && canExpand && (
        <div className="px-3 pb-3">
          <div className="rounded-lg bg-white p-4 dark:bg-gray-900">
            <h4 className="mb-3 text-center text-sm font-semibold text-gray-900 dark:text-white">Deliverables</h4>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {deliverables.map((d) => {
                const isSelected = d.quantity != null;
                return (
                  <div
                    key={d.id}
                    className={`relative flex flex-col items-center justify-center rounded-xl border-2 p-3 ${
                      isSelected
                        ? 'border-green-500 bg-green-50 dark:border-green-500 dark:bg-green-900/30'
                        : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700/30'
                    }`}
                  >
                    {isSelected && (
                      <Check className="absolute right-2 top-2 h-4 w-4 text-green-600 dark:text-green-400" strokeWidth={3} />
                    )}
                    <span className="mb-1 text-2xl">{d.icon || '📦'}</span>
                    <h3
                      className={`text-center text-xs font-semibold ${
                        isSelected ? 'text-green-800 dark:text-green-200' : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {d.name}
                    </h3>
                    {d.quantity > 1 && (
                      <span className="mt-2 rounded-full bg-white px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-900 dark:bg-gray-800 dark:text-white">
                        ×{d.quantity}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartProgramActivityRow;
